import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TournamentCreateModel,
  WeightCategoryInput,
  AgeCategoryInput,
  TournamentClient,
  Gender,
} from '../../utils/apiClient';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { useAuth } from '../../components/Auth/AuthContext';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { DateLocal, DateType } from '../../common/components/DateLocal/DateLocal';
import {
  AgeCategoryDraft,
  WeightCategoryDraft,
  validateGenderedTournamentDrafts,
} from '../../utils/categoryResolver';
import {
  buildGenderedCategoryInputs,
  GenderedCategoriesSection,
} from '../CategoryLimitsEditor/GenderedCategoriesSection';

const defaultBoysWeight: WeightCategoryDraft[] = [
  { sortOrder: 1, maxWeight: 30, isOpenEnded: false, gender: Gender.Male },
  { sortOrder: 2, maxWeight: 34, isOpenEnded: false, gender: Gender.Male },
  { sortOrder: 3, maxWeight: 38, isOpenEnded: false, gender: Gender.Male },
  { sortOrder: 4, maxWeight: 42, isOpenEnded: false, gender: Gender.Male },
  { sortOrder: 5, isOpenEnded: true, gender: Gender.Male },
];

const defaultBoysAge: AgeCategoryDraft[] = [
  { sortOrder: 1, maxAge: 13, isOpenEnded: false, gender: Gender.Male, name: 'Юноши' },
  { sortOrder: 2, isOpenEnded: true, name: 'Старшие', gender: Gender.Male },
];

const defaultGirlsWeight: WeightCategoryDraft[] = [
  { sortOrder: 1, maxWeight: 28, isOpenEnded: false, gender: Gender.Female },
  { sortOrder: 2, maxWeight: 32, isOpenEnded: false, gender: Gender.Female },
  { sortOrder: 3, maxWeight: 36, isOpenEnded: false, gender: Gender.Female },
  { sortOrder: 4, maxWeight: 40, isOpenEnded: false, gender: Gender.Female },
  { sortOrder: 5, isOpenEnded: true, gender: Gender.Female },
];

const defaultGirlsAge: AgeCategoryDraft[] = [
  { sortOrder: 1, maxAge: 13, isOpenEnded: false, gender: Gender.Female, name: 'Девушки' },
  { sortOrder: 2, isOpenEnded: true, name: 'Старшие', gender: Gender.Female },
];

const CreateTournament: React.FC = () => {
  const appContext = React.useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const tournamentClient = new TournamentClient(ApiUrl, axios);

  const [tournament, setTournament] = useState<TournamentCreateModel>(new TournamentCreateModel({
    name: '',
    location: '',
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
    registrationEndDate: new Date(),
    durationInMinutes: 3,
    weightCategories: [],
    ageCategories: [],
  }));

  const [boysWeight, setBoysWeight] = useState<WeightCategoryDraft[]>(defaultBoysWeight);
  const [boysAge, setBoysAge] = useState<AgeCategoryDraft[]>(defaultBoysAge);
  const [girlsWeight, setGirlsWeight] = useState<WeightCategoryDraft[]>(defaultGirlsWeight);
  const [girlsAge, setGirlsAge] = useState<AgeCategoryDraft[]>(defaultGirlsAge);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/tournaments');
    }
  }, [isAdmin, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTournament(new TournamentCreateModel({ ...tournament, [name]: value }));
  };

  const setDate = (field: 'startDate' | 'endDate' | 'registrationEndDate') => (date: Date | undefined) => {
    if (date) setTournament(new TournamentCreateModel({ ...tournament, [field]: date }));
  };

  const validateForm = (): boolean => {
    if (!tournament.name.trim()) {
      setError('Название турнира обязательно');
      return false;
    }

    if (!tournament.location.trim()) {
      setError('Место проведения обязательно');
      return false;
    }

    const startDate = new Date(tournament.startDate).StartOfDay();
    const endDate = new Date(tournament.endDate).StartOfDay();
    const regEndDate = new Date(tournament.registrationEndDate).StartOfDay();
    const today = new Date().StartOfDay();

    if (startDate < today) {
      setError('Дата начала не может быть в прошлом');
      return false;
    }

    if (endDate < startDate) {
      setError('Дата окончания не может быть раньше даты начала');
      return false;
    }

    if (regEndDate > startDate) {
      setError('Дата окончания регистрации должна быть раньше даты начала турнира');
      return false;
    }

    if (tournament.durationInMinutes < 1) {
      setError('Продолжительность поединка должна быть не менее 1 минуты');
      return false;
    }

    const categoryError = validateGenderedTournamentDrafts(boysWeight, boysAge, girlsWeight, girlsAge);
    if (categoryError) {
      setError(categoryError);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validateForm()) return;

    const merged = buildGenderedCategoryInputs(boysWeight, boysAge, girlsWeight, girlsAge);

    const payload = new TournamentCreateModel({
      ...tournament,
      weightCategories: merged.weightCategories.map(w => new WeightCategoryInput({
        sortOrder: w.sortOrder,
        maxWeight: w.isOpenEnded ? undefined : w.maxWeight,
        isOpenEnded: w.isOpenEnded,
        gender: w.gender,
      })),
      ageCategories: merged.ageCategories.map(a => new AgeCategoryInput({
        sortOrder: a.sortOrder,
        maxAge: a.isOpenEnded ? undefined : a.maxAge,
        isOpenEnded: a.isOpenEnded,
        name: a.name?.trim() || '',
        gender: a.gender,
      })),
    });

    try {
      setLoading(true);
      const result = await tournamentClient.createTournament(payload);
      setSuccess(true);
      setTimeout(() => navigate(`/tournaments/${result.id}`), 1500);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-tournament">
      <h2 className="page-heading">Создание нового турнира</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">Турнир успешно создан! Перенаправление...</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Название турнира:</label>
          <input
            type="text"
            name="name"
            value={tournament.name}
            onChange={handleInputChange}
            required
            placeholder="Например: Чемпионат Москвы по дзюдо 2025"
          />
        </div>

        <div className="form-group">
          <label>Место проведения:</label>
          <input
            type="text"
            name="location"
            value={tournament.location}
            onChange={handleInputChange}
            required
            placeholder="Например: Спортивный комплекс 'Олимпийский', Москва"
          />
        </div>

        <div className="form-group">
          <label>Дата начала:</label>
          <DateLocal type={DateType.date} value={new Date(tournament.startDate)} onChange={setDate('startDate')} width={200} isClearable={false} />
        </div>

        <div className="form-group">
          <label>Дата окончания:</label>
          <DateLocal type={DateType.date} value={new Date(tournament.endDate)} onChange={setDate('endDate')} width={200} isClearable={false} />
        </div>

        <div className="form-group">
          <label>Дата окончания регистрации:</label>
          <DateLocal type={DateType.date} value={new Date(tournament.registrationEndDate)} onChange={setDate('registrationEndDate')} width={200} isClearable={false} />
        </div>

        <div className="form-group">
          <label>Продолжительность поединка (минуты):</label>
          <input
            type="number"
            name="durationInMinutes"
            value={tournament.durationInMinutes}
            onChange={handleInputChange}
            required
            min="1"
            max="15"
          />
        </div>

        <div className="form-group">
          <label>Главный судья (ФИО для протоколов):</label>
          <input
            type="text"
            name="chiefJudgeName"
            value={tournament.chiefJudgeName ?? ''}
            onChange={handleInputChange}
            placeholder="Иванов Иван Иванович"
            maxLength={150}
          />
        </div>

        <div className="form-group">
          <label>Главный секретарь (ФИО для протоколов):</label>
          <input
            type="text"
            name="chiefSecretaryName"
            value={tournament.chiefSecretaryName ?? ''}
            onChange={handleInputChange}
            placeholder="Петров Пётр Петрович"
            maxLength={150}
          />
        </div>

        <div className="form-group categories">
          <label>Категории по полу</label>
          <p className="field-description">Настройте вес и возраст отдельно для мальчиков и девочек. Можно заполнить только один пол.</p>
          <GenderedCategoriesSection
            gender={Gender.Male}
            title="Мальчики"
            weightDrafts={boysWeight}
            ageDrafts={boysAge}
            onWeightChange={setBoysWeight}
            onAgeChange={setBoysAge}
          />
          <GenderedCategoriesSection
            gender={Gender.Female}
            title="Девочки"
            weightDrafts={girlsWeight}
            ageDrafts={girlsAge}
            onWeightChange={setGirlsWeight}
            onAgeChange={setGirlsAge}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn" disabled={loading}>{loading ? 'Создание...' : 'Создать турнир'}</button>
          <button type="button" className="btn btn--back" onClick={() => navigate('/tournaments')}>Отмена</button>
        </div>
      </form>
    </div>
  );
};

export default CreateTournament;
